import React, {useEffect, useState} from 'react';
import {useSelector, useDispatch} from "react-redux";
import {useParams, useNavigate} from "react-router-dom";
import {Alert, Button, Form} from "react-bootstrap";
import {Helmet} from "react-helmet";
import * as t from "counterpart";
import moment from "moment";
import {loadCart, placeOrder} from "../actions";

import ShippingMethods from "./ShippingMethods";
import Addresses from "./Addresses";
import Locations from "./Locations";
import PaymentMethods from "./PaymentMethods";
import Coupon from "./Coupon";

import "./PlaceOrder.scss";
import Select from "react-select";
import ProductBadges from "../../Commerce/Blocks/ProductBadges";

function PlaceOrder()
{
	const {cart, portal} = useSelector(({cart, portal}) => ({cart, portal}));
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const store = useParams().store;

	// order data
	const [order, setOrder] = useState({platform: "web", name: "", phone: "", email: "", delivery_name: "", delivery_phone: "", delivery_email: "", delivery_time: "", consent: false});

	// steps feedback
	const contactInfoDone = order.name && order.phone && order.email;
	const shippingDone = !cart.isShippable || (order.shipping_method_id && (!order.addressAddress || order.address_id));
	const canContinue = !cart.isLoading && contactInfoDone && shippingDone;

	// load initial data
	useEffect(() => dispatch(loadCart()), [dispatch]);

	//
	useEffect(() => {
		if(portal.user) {
			const {full_name, email, mobile} = portal.user || {};
			setOrder(order => {
				return {...order, name: full_name, email, phone: mobile, delivery_name: full_name, delivery_email: email, delivery_phone: mobile};
			});
		}
	}, [portal.user]);

	//
	const onContactChange = ({target}) => setOrder({...order, [target.name]: target.value});

	// send order request
	const onPlaceOrder = () => {
		if(!order.consent) {
			alert("You must agree to the terms before you can proceed");
			return;
		}

		if(!cart.isLoading) {
			dispatch(placeOrder(order, navigate, store));
		}
	};

	const length = cart.items.length;
	const error = cart.error;

	useEffect(() => window.scroll(0, 0), [error]);

	let total = 0;
	let totalSaved = 0;
	let totalCashBack = 0;

	if(order.coupon) {
		total -= order.coupon.discount;
	}

	const renderItem = (item, cashBack) =>
	(
		<div key={item.cart_id} className={"product "+(cart.productError === item.id ? "error" : "")}>
			<div className="image" style={{backgroundImage: "url('"+item.thumbnail+"')"}} />
			<h3>{item.name}</h3>
			<h6>{t("Quantity")} {item.cart_quantity}</h6>
			<div className="price"> {item.current_price === 0 ? t("Free") : (item.current_price + (item.cart_options_price || 0) - cashBack).toLocaleString()+" "+t(window.portalSetting("commerce.currency"))}</div>
			{item.cart_expiration && <h5 style={{color: "red"}}>{t("Expires")} {moment(item.cart_expiration).fromNow()}</h5>}
			<ProductBadges product={item} />
		</div>
	);

	return (
		<div id="place-order-page" className="container page-body">
			<h1><i className="fa fa-shopping-cart" />{t("Cart")} <span>({length} {t("Items")})</span></h1>
			<Helmet>
				<title>{t("Place Your Order")}</title>
			</Helmet>

			{length === 0 && <h1>{t("There are no items in your cart.")}</h1>}
			{length !== 0 &&
			<div>
				{/* products */}
				<div id="products">
					{cart.items.map(item => {
						const cashBack = item.cash_back || 0;
						total += (item.current_price + (item.cart_options_price || 0) - cashBack) * item.cart_quantity;
						totalSaved += item.saved_amount * item.cart_quantity;
						totalCashBack += cashBack * item.cart_quantity;

						const gifts = (order.coupon?.disableGifts ? [] : item.gifts) || [];

						return [
							renderItem(item, cashBack),
							...gifts.map(gift => {
								gift.cart_quantity = item.cart_quantity;
								gift.saved_amount = item.current_price;
								gift.cart_price = 0;
								gift.current_price = 0;
								gift.cash_back = 0;
								return renderItem(gift, 0, false);
							})
						]
					})}
				</div>

				{/* steps */}
				<div id="steps">
					<div className="step active">
						<div><i className="fa fa-bag-shopping" /></div>
						<span>{t("Place Your Order")}</span>
					</div>
					<div className="line active"></div>
					<div className="step">
						<div><i className="fa fa-credit-card" /></div>
						<span>{t("Payment")}</span>
					</div>
					<div className="line"></div>
					<div className="step">
						<div><i className="fa fa-house-circle-check" /></div>
						<span>{t("Delivery")}</span>
					</div>
					<div className="line"></div>
					<div className="step">
						<div><i className="fa fa-check-double" /></div>
						<span>{t("Delivered")}</span>
					</div>
				</div>

				{/* validation */}
				{error &&
				<Alert variant="danger">
					{(typeof error === "object") ? Object.keys(error).map(field =>
						<div key={field}>
							<h4>{t(field)}</h4>
							<ul>
								{(error[field] || []).map((msg, i) => <li key={i}>{t("validation_"+msg)}</li>)}
							</ul>
						</div>
					) : error }
				</Alert>}

				{/* contact-info */}
				<div id="contact-info">
					<h3><span>1</span>{t("Contact Information")}</h3>
					<div className="row">
						<Form.Group controlId="name" className="col-md-4">
							<Form.Label>{t("Full Name")}</Form.Label>
							<Form.Control name="name" value={order.name} onChange={onContactChange} />
						</Form.Group>

						<Form.Group controlId="phone" className="col-md-4">
							<Form.Label>{t("Mobile")}</Form.Label>
							<Form.Control name="phone" value={order.phone} onChange={onContactChange} />
						</Form.Group>

						<Form.Group controlId="email" className="col-md-4">
							<Form.Label>{t("Email")}</Form.Label>
							<Form.Control name="email" value={order.email} onChange={onContactChange} />
						</Form.Group>
					</div>
				</div>

				{cart.isShippable &&
				<div className="row">
					{<ShippingMethods title={<h3><span>2</span>{t("Shipping Method")}</h3>} className="col-md-6" onChange={m => setOrder(order => {
						let newOrder = {...order, shipping_method_id: m.id, addressRequired: m.address_required};
						if(!newOrder.addressRequired) {
							delete newOrder.address_id;
						}
						return newOrder;
					})} />}
					{order.addressRequired && <Addresses onChange={id => setOrder(order => ({...order, address_id: id}))} />}
					{!order.addressRequired && <Locations onChange={id => setOrder(order => ({...order, location_id: id}))} />}

					{order.addressRequired &&
					<div id="contact-info">
						<h3>{t("Delivery Information")}</h3>
						<div className="row">
							<Form.Group controlId="name" className="col-md-3">
								<Form.Label>{t("Full Name")}</Form.Label>
								<Form.Control name="delivery_name" value={order.delivery_name} onChange={onContactChange} />
							</Form.Group>

							<Form.Group controlId="phone" className="col-md-3">
								<Form.Label>{t("Mobile")}</Form.Label>
								<Form.Control name="delivery_phone" value={order.delivery_phone} onChange={onContactChange} />
							</Form.Group>

							<Form.Group controlId="email" className="col-md-3">
								<Form.Label>{t("Email")}</Form.Label>
								<Form.Control name="delivery_email" value={order.delivery_email} onChange={onContactChange} />
							</Form.Group>

							<Form.Group controlId="email" className="col-md-3">
								<Form.Label>{t("Delivery Time")}</Form.Label>
								<Select
									placeholder={t("Select")}
									value={order.delivery_time ? {value: order.delivery_time, label: order.delivery_time} : null}
									options={[{value: "09:00 - 12:00 "+t("AM"), label: "09:00 - 12:00 "+t("AM")}, {value: "04:00 - 09:00 "+t("PM"), label: "04:00 - 09:00 "+t("PM")}]}
									onChange={l => onContactChange({target: {name: "delivery_time", value: l.value}})}
								/>
							</Form.Group>
						</div>
					</div>}
				</div>}

				<div className="row">
					<PaymentMethods total={total} onChange={id => setOrder(order => ({...order, payment_method_id: id}))} />
					<Coupon
						className="col-sm-4"
						onChange={coupon => setOrder(order => ({...order, coupon, coupon_code: coupon?.coupon_code}))}
					/>
				</div>

				<div id="total" className="row">
					{totalSaved !== 0 && <div className="col-6 saved">{t("Total Saved")}</div>}
					{totalSaved !== 0 && <div className="col-6 saved"> {totalSaved.toLocaleString()}</div>}
					<h6 className="col-6">{t("VAT")}</h6>
					<div className="col-6"> {((total / 1.15) * 0.15).toLocaleString()}</div>
					<h5 className="col-6">{t("Total")}</h5>
					<div className="col-6"> {total.toLocaleString()}</div>
				</div>

				<Form.Check
					id="terms"
					type="checkbox"
					onChange={e => setOrder({...order, consent: e.target.checked})}
					label={<>{t("Click here to indicate that you have read and agree to")} <a href={`/${store}/terms`} target="_blank">{t("the Terms and Conditions agreement")}</a></>}
				/>

				<Button id="continue" disabled={!canContinue} onClick={onPlaceOrder}>
					{!cart.isLoading && <><i className="fa fa-money-bill" /> {t("Place Your Order")}</>}
					{cart.isLoading && <><i className="fa fa-spinner fa-spin" /> {t("Loading")}</>}
				</Button>
			</div>}
		</div>
	)
}

export default PlaceOrder;
